import { reaction, toJS } from 'mobx';

export enum PersistStorage {
  Local = 'local',
  Session = 'session',
}

interface CreatePersistStoreParams<T extends object> {
  store: T;
  storageKey: string;
  fieldsToPersist: (keyof T)[];
  storageType?: PersistStorage;
}

export function createPersistStore<T extends object>({
  store,
  storageKey,
  fieldsToPersist,
  storageType = PersistStorage.Local,
}: CreatePersistStoreParams<T>): T {
  const storage = PersistStorage.Local ? localStorage : sessionStorage;
  const storedString = storage.getItem(storageKey);

  if (storedString) {
    try {
      const storedObject = JSON.parse(storedString);

      fieldsToPersist.forEach((fieldName: keyof T) => {
        const storedValue = storedObject[fieldName];

        if (storedValue !== undefined) {
          store[fieldName] = storedValue;
        }
      });
    } catch (error) {
      console.error(`[Persist] Failed to parse data for "${storageKey}"`, error);
    }
  }

  reaction(
    () => {
      const dataToSave: Partial<Record<keyof T, unknown>> = {};

      fieldsToPersist.forEach((fieldName: keyof T) => {
        dataToSave[fieldName] = toJS(store[fieldName]);
      });

      return dataToSave;
    },
    (dataToSave) => {
      storage.setItem(storageKey, JSON.stringify(dataToSave));
    },
  );

  return store;
}
